import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { filterComics, sortByHot } from "@/utils/comic";
import ComicCard from "@/components/comic/ComicCard";
import SkeletonCard from "@/components/comic/SkeletonCard";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;
const hotKeywords = ["校园", "奇幻", "恋爱", "治愈", "契约", "双向奔赴", "慢热"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [inputValue, setInputValue] = useState(keyword);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setInputValue(keyword);
  }, [keyword]);

  useEffect(() => {
    setLoading(true);
    setVisible(PAGE_SIZE);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [keyword]);

  useSeo({
    title: keyword
      ? `「${keyword}」搜索结果 - 韩漫免费漫画在线观看阅读双女主`
      : "搜索漫画 - 韩漫免费漫画在线观看阅读双女主",
    description: keyword
      ? `在双女漫搜索「${keyword}」的相关双女主韩漫结果，免费在线阅读最新章节。`
      : "输入关键词搜索你喜欢的双女主韩漫，支持按标题、作者与题材匹配。",
    keywords: "韩漫搜索,双女主漫画,免费漫画在线观看",
    canonicalPath: "/search",
  });

  const results = useMemo(
    () =>
      filterComics(comics, {
        genre: "全部",
        status: "全部",
        tag: "全部",
        sort: "updated",
        keyword,
      }),
    [keyword],
  );

  const hotList = useMemo(() => sortByHot(comics).slice(0, 4), []);
  const shown = results.slice(0, visible);
  const hasMore = visible < results.length;

  const submit = (value: string) => {
    const q = value.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams(new URLSearchParams());
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
      <nav className="mb-6 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">搜索结果</span>
      </nav>

      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索漫画</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(inputValue);
        }}
        className="mt-5 flex flex-col gap-3 sm:flex-row"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400">
          <i className="ri-search-line text-lg text-foreground-400" />
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="输入漫画名称、作者或题材关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-500">热门搜索：</span>
        {hotKeywords.map((word) => (
          <button
            key={word}
            type="button"
            onClick={() => submit(word)}
            className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {word}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {keyword && (
          <p className="mb-5 text-sm text-foreground-600">
            关键词「<span className="font-medium text-foreground-950">{keyword}</span>」共找到
            <span className="mx-1 font-semibold text-primary-600">{results.length}</span>部漫画
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300" />
            <p className="mt-4 text-sm text-foreground-600">未找到相关漫画，请尝试其他关键词</p>
            <button
              type="button"
              onClick={() => {
                setInputValue("");
                setSearchParams(new URLSearchParams());
              }}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
            >
              清空关键词
            </button>

            <div className="mx-auto mt-12 max-w-[1080px] px-4 text-left">
              <h2 className="mb-5 font-heading text-lg font-bold text-foreground-950">热门推荐，也许你会喜欢</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4" data-product-shop>
                {hotList.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4" data-product-shop>
              {shown.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  加载更多（剩余 {results.length - visible} 部）
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}